<script setup lang="ts">
/**
 * An imported page's settings: where it came from, the frame picked on it,
 * and the fonts it is drawn in.
 *
 * Text is rewritten on the page itself; this panel says which frame is picked,
 * whether it is as printed or rewritten, what in it the fonts cannot draw, and
 * gives the way back to the print.
 */
import { useI18n } from 'vue-i18n'

import { computed } from 'vue'

import FontStatusChip from '@/components/fonts/FontStatusChip.vue'
import { useFontsDialog } from '@/components/fonts/fontsDialog'
import { fontStatus, missingCharacters, type ImportedFont } from '@/domain/facsimile/fonts'
import { currentRuns, plainText, restore } from '@/domain/facsimile/text'
import type { FixedPageSection } from '@/domain/section'
import { useFrameSelection } from '@/sections/fixedPage/frameSelection'
import { useReportStore } from '@/stores/report'

const PREVIEW_CHARS = 220

const { t } = useI18n()

const props = defineProps<{ section: FixedPageSection }>()
const emit = defineEmits<{ change: [section: FixedPageSection] }>()

const report = useReportStore()
const selection = useFrameSelection()
const fontsDialog = useFontsDialog()

// Read through the props each time: this panel is reused when another
// imported page is selected, and must follow it.
const chosen = computed(() => {
  const id = selection.chosenIn(props.section.id)
  return props.section.frames.find((frame) => frame.id === id) ?? null
})

const chosenText = computed(() => {
  const text = chosen.value ? plainText(currentRuns(chosen.value)) : ''
  return text.length > PREVIEW_CHARS ? `${text.slice(0, PREVIEW_CHARS)}…` : text
})

const fontsByKey = computed(() => new Map(report.fonts.map((font) => [font.key, font])))

/** The characters of the picked frame some font cannot draw with its own glyphs. */
const missing = computed(() => {
  if (!chosen.value) return ''
  const found = new Set<string>()
  for (const run of currentRuns(chosen.value)) {
    for (const character of missingCharacters(run.text, fontsByKey.value.get(run.font)))
      found.add(character)
  }
  return [...found].join(' ')
})

/** The fonts this page uses, worst first: the ones that need a decision lead. */
const pageFonts = computed<ImportedFont[]>(() => {
  const keys = new Set(
    props.section.frames.flatMap((frame) =>
      frame.lines.flatMap((line) => line.runs.map((run) => run.font)),
    ),
  )
  const order = { missing: 0, partial: 1, complete: 2 }
  return report.fonts
    .filter((font) => keys.has(font.key))
    .sort((a, b) => order[fontStatus(a)] - order[fontStatus(b)])
})

const overflowLines = computed(() =>
  chosen.value ? selection.overflowOf(props.section.id, chosen.value.id) : 0,
)

const rewrittenCount = computed(() => props.section.frames.filter((frame) => frame.edited).length)

function restoreChosen(): void {
  const frame = chosen.value
  if (!frame) return
  emit('change', {
    ...props.section,
    frames: props.section.frames.map((one) => (one.id === frame.id ? restore(one) : one)),
  })
}

function restoreAll(): void {
  emit('change', { ...props.section, frames: props.section.frames.map(restore) })
}
</script>

<template>
  <div class="inspector">
    <p class="source">
      {{
        t('inspector.fixedPage.source', {
          file: section.sourceFile || '—',
          page: section.sourcePage,
        })
      }}
    </p>

    <section class="card">
      <h3 class="card__title">{{ t('inspector.fixedPage.frame') }}</h3>
      <template v-if="chosen">
        <p class="card__state" :class="{ 'card__state--edited': chosen.edited }">
          {{
            chosen.edited ? t('inspector.fixedPage.rewritten') : t('inspector.fixedPage.asPrinted')
          }}
        </p>
        <blockquote class="card__text">{{ chosenText }}</blockquote>
        <p v-if="overflowLines > 0" class="card__warning" role="status">
          {{ t('inspector.fixedPage.overflow', { lines: overflowLines }, overflowLines) }}
        </p>
        <p v-if="missing" class="card__warning" role="status">
          {{ t('inspector.fixedPage.missing', { characters: missing }) }}
          <button type="button" class="link" @click="fontsDialog.show()">
            {{ t('inspector.fixedPage.fixFonts') }}
          </button>
        </p>
        <div class="card__actions">
          <button
            type="button"
            class="b-btn b-btn--outline"
            :disabled="!report.editable"
            @click="selection.edit(section.id, chosen.id)"
          >
            {{ t('inspector.fixedPage.rewrite') }}
          </button>
          <button
            v-if="chosen.edited"
            type="button"
            class="b-btn b-btn--outline"
            :disabled="!report.editable"
            @click="restoreChosen"
          >
            {{ t('inspector.fixedPage.restore') }}
          </button>
        </div>
      </template>
      <p v-else class="card__hint">{{ t('inspector.fixedPage.pickHint') }}</p>
    </section>

    <label class="b-switch">
      <input
        type="checkbox"
        :checked="selection.outlined.value"
        @change="selection.outlined.value = ($event.target as HTMLInputElement).checked"
      />
      <span class="b-switch__track" />
      <span class="switch-label">{{ t('inspector.fixedPage.outline') }}</span>
    </label>

    <section class="card">
      <h3 class="card__title">{{ t('inspector.fixedPage.fonts') }}</h3>
      <ul class="fonts">
        <li v-for="font in pageFonts" :key="font.key" class="fonts__row">
          <span class="fonts__name">{{ font.key }}</span>
          <FontStatusChip :status="fontStatus(font)" />
        </li>
      </ul>
      <button type="button" class="b-btn b-btn--outline" @click="fontsDialog.show()">
        {{ t('inspector.fixedPage.manageFonts') }}
      </button>
    </section>

    <button
      v-if="rewrittenCount > 0"
      type="button"
      class="link link--quiet"
      :disabled="!report.editable"
      @click="restoreAll"
    >
      {{ t('inspector.fixedPage.restoreAll', { count: rewrittenCount }) }}
    </button>
  </div>
</template>

<style scoped>
.inspector {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

.source {
  margin: 0;
  color: var(--ink-soft);
  font-size: var(--text-sm);
}

.card {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  padding: var(--sp-4);
  border-radius: var(--radius);
  background: var(--panel-raised);
  box-shadow: inset 0 0 0 1px var(--hairline);
}

.card__title {
  margin: 0;
  color: var(--ink-faint);
  font-size: var(--text-xs);
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.card__state {
  margin: 0;
  color: var(--accent);
  font-size: var(--text-sm);
  font-weight: 600;
}

.card__state--edited {
  color: var(--warn);
}

.card__text {
  margin: 0;
  padding: var(--sp-4) var(--sp-5);
  border-radius: var(--radius);
  background: var(--panel-hover);
  color: var(--ink);
  font-size: var(--text-sm);
  line-height: 1.5;
}

.card__warning {
  margin: 0;
  color: var(--warn);
  font-size: var(--text-sm);
}

.card__hint {
  margin: 0;
  color: var(--ink-faint);
  font-size: var(--text-sm);
}

.card__actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-3);
}

.fonts {
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
  margin: 0;
  padding: 0;
  list-style: none;
}

.fonts__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-3);
}

.fonts__name {
  overflow: hidden;
  color: var(--ink);
  font-size: var(--text-sm);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.switch-label {
  font-size: var(--text-base);
}

.link {
  padding: 0;
  border: 0;
  background: none;
  color: var(--accent);
  font: inherit;
  text-decoration: underline;
  cursor: pointer;
}

.link--quiet {
  align-self: flex-start;
  color: var(--ink-soft);
  font-size: var(--text-sm);
}
</style>
